import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { StudentService } from '@app/core';
import { StudentSummary } from '@app/core/models';
import { SHARED_MODULES } from '@app/shared';
import { UserPhotoComponent } from '@app/shared/modules/user-photo/user-photo.component';
import { notNull, safeEmptyList } from '@app/shared/utils';
import { switchDelay } from '@app/shared/utils/operators';
import { first, isEmpty, size, some, uniqBy } from 'lodash';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SidenavDailyShow } from '../modal-daily/base';

export interface SpecificStudents {
  studentId: number;
  groupId: number;
}
@Component({
  selector: 'app-list-specific-students',
  imports: [SHARED_MODULES, UserPhotoComponent],
  templateUrl: './list-specific-students.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ListSpecificStudentsComponent),
      multi: true,
    },
  ],
})
export class ListSpecificStudentsComponent implements ControlValueAccessor, OnDestroy {
  private readonly _studentService = inject(StudentService);
  @Output() public selectAll = new EventEmitter<boolean>();
  public selecteds: SpecificStudents[] = [];
  public students$: Observable<any>;
  public loading$: Observable<boolean>;
  public targets$: Observable<SidenavDailyShow[]>;
  public searchCtrl = new FormControl();
  public trackById = (index: any, data: any) => data.id;
  private _targets$ = new BehaviorSubject<SidenavDailyShow[]>([]);
  private _loading$ = new BehaviorSubject(false);

  constructor() {
    this.loading$ = this._loading$.asObservable().pipe(switchDelay());
    this.targets$ = this._targets$.asObservable();
    this.loadData();
  }

  @Input()
  public set targets(value: SidenavDailyShow[]) {
    this._targets$.next(value);
  }

  public ngOnDestroy(): void {
    this._targets$.complete();
    this._loading$.complete();
  }

  public writeValue(students: SpecificStudents[]) {
    this.selecteds = students;
  }

  public registerOnChange(fn: any) {
    this.changeValue = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public onSelectAll(checked: boolean, students: any[]) {
    if (checked) {
      this.selecteds = students.map((student) => ({ studentId: student.id, groupId: student.group_id }));
    } else {
      this.selecteds = [];
    }
    this.selectAll.emit(isEmpty(this.selecteds));
    this.changeValue(this.selecteds);
  }

  public onSelect(student: any) {
    if (this.isSelected(student.id)) {
      this.selecteds = this.selecteds.filter(({ studentId }) => studentId !== student.id);
    } else {
      this.selecteds.push({ studentId: student.id, groupId: student.group_id });
    }
    this.onTouched();
    this.selectAll.emit(false);
    this.changeValue(this.selecteds);
  }

  public isSelected(studentId: number): boolean {
    return this.selecteds.some((data) => data.studentId === studentId);
  }

  public isAllchecked(students: any[]): boolean {
    const length = size(students);
    return length > 1 && length === size(this.selecteds);
  }

  public isIndeterminateStatus(students: any[]): boolean {
    return !this.isAllchecked(students) && !isEmpty(this.selecteds);
  }

  private loadData() {
    this.students$ = this._targets$.pipe(
      notNull(),
      tap(() => this._loading$.next(true)),
      switchMap((targets) => (isEmpty(targets) ? of([]) : this.loadStudents(targets))),
      tap((data) => this.sanitizeSelecteds(data)),
      tap(() => this._loading$.next(false)),
    );
  }

  private loadStudents(targets: SidenavDailyShow[] = []): Observable<StudentSummary[]> {
    if (!isEmpty(targets)) {
      const entityId = first(targets)?.entity.id;
      const group_id = targets.map((target) => target.group.id);
      const showEntity = size(uniqBy(targets, 'entity.id')) > 1;
      return this._studentService.getStudentsByEntity(entityId, { group_id }).pipe(
        safeEmptyList(),
        map((students) =>
          students.map((student) => {
            const target = targets.find((target) => some(student.student_groups, ['turma_id', target.group.id]));
            return {
              ...student,
              showEntity,
              group_id: target.group.id,
              target,
            };
          }),
        ),
      );
    }

    return of([]);
  }

  private sanitizeSelecteds(students: any[]) {
    this.selecteds = this.selecteds.filter(({ studentId }) => students.some((student) => student.id === studentId));
    this.selectAll.emit(this.isAllchecked(students));
    this.changeValue(this.selecteds);
  }

  private onTouched = () => true;
  private changeValue = (_: any) => true;
}
